import type { Ref } from 'vue';
import {
  computed,
  ref,
} from 'vue';

import type { IAffiliateProgramPromoCodePayload } from '@modules/affiliate-program-promo-codes/submodules/create/types';
import type { IApiFormErrors } from '@modules/core/services/query/types';
import type {
  FormSchema,
  VFormRef,
} from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import { TRANSLATION_GROUP_AFFILIATE_PROGRAM_PROMO_CODES } from '@modules/affiliate-program-promo-codes/constants';
import { useAffiliateProgramPromoCodeCreateMutation } from '@modules/affiliate-program-promo-codes/submodules/create/mutations';
import { useAffiliateProgramPromoTrackersListQuery } from '@modules/affiliate-program-promo-codes/submodules/create/queries';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';

export interface AffiliateProgramPromoCodesCreateComposable {
  form: Ref<VFormRef | undefined>;
  formSchema: Ref<FormSchema<IAffiliateProgramPromoCodePayload>>;
  isLoadingTrackers: Ref<boolean>;
  isLoading: Ref<boolean>;
  onSubmit(payload: IAffiliateProgramPromoCodePayload): Promise<void>;
  errors: Ref<IApiFormErrors | undefined>;
  clearError(key: string): void;
}

export default function useAffiliateProgramPromoCodesCreate(): AffiliateProgramPromoCodesCreateComposable {
  const form = ref<VFormRef>();

  const { getTranslation } = useI18n();
  const { data: trackersList, isLoading: isLoadingTrackers } = useAffiliateProgramPromoTrackersListQuery();
  const {
    isLoading,
    mutateAsync,
    errors,
    clearError,
  } = useAffiliateProgramPromoCodeCreateMutation();

  const trackerOptions = computed<IFormSelectInputOption<number>[]>(() => {
    if (!trackersList.value) {
      return [];
    }

    return trackersList.value.list_trackers.map((item) => ({
      value: item.id,
      label: `${item.value}`,
    }));
  });

  const formSchema = computed<FormSchema<IAffiliateProgramPromoCodePayload>>(() => ({
    code: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_PROMO_CODES, 'FORM_PROMO_CODE').value,
      required: true,
    },
    serial_id: {
      type: EFormInputTypes.SELECT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_PROMO_CODES, 'FORM_TRACKER').value,
      required: true,
      options: trackerOptions.value,
    },
  }));

  async function onSubmit(payload: IAffiliateProgramPromoCodePayload): Promise<void> {
    await mutateAsync(payload);
    form.value?.reset();
  }

  return {
    form,
    formSchema,
    isLoadingTrackers,
    isLoading,
    onSubmit,
    errors,
    clearError,
  };
}
